import Route from '@ember/routing/route';
import { service } from '@ember/service';
import initializeVdrClient from 'scaleflow/utils/aws-clients/s3VDRClient';
import initializeMediaClient from 'scaleflow/utils/aws-clients/s3MediaClient';

export const unprotectedRoutes = [
  'login',
  'logout',
  undefined,
  'index',
  'authenticate',
  'reset-password',
];
class ApplicationRoute extends Route {
  @service session;
  @service router;
  @service tracker;
  @service roadmap;
  @service('data-socket') socketService;
  @service('beta-toggle') betaToggleService;
  @service initiativeSlideover;
  @service('page-loader-ui') pageLoader;
  @service organisation;
  @service currentUser;
  @service versionChecker;
  @service store;
  @service intl;
  @service('feature-access') featureAccessService;
  @service chatv2;
  @service routerHistory;

  queryParams = [
    {
      roadmap_id: {
        refreshModel: true,
      },
      initiative_id: {
        refreshModel: false,
      },
      state: {
        refreshModel: false,
      },
    },
  ];

  constructor() {
    super(...arguments);
    // Accessing the service instantiates it, which registers its own
    // routeWillChange/routeDidChange listeners for origin tracking.
    void this.routerHistory;

    this.router.on('routeWillChange', async () => {
      this.pageLoader.show();
    });

    this.router.on('routeDidChange', () => {
      this.pageLoader.hide();
      const url = this.router.currentURL;
      const title = this.router.currentRouteName || 'unknown';
      this.tracker.trackPage({ url, title });
      if (this.versionChecker.app_should_update) {
        window.location.reload();
        return;
      }
      // Re-check here rather than on a timer: an idle tab then costs nothing,
      // and a busy one is already making far more requests than this. Navigating
      // is also the only moment a reload is free, since the page being left is
      // discarded anyway — so this is exactly where the answer needs to be fresh.
      void this.versionChecker.checkIfAppShouldUpdate();
    });
  }

  async setupSessionAndRefreshIfNeeded() {
    await this.session.setup();
    const isExpiredSession = this.session.tokenIsExpired;
    if (isExpiredSession) {
      await this.session.refreshToken();
    }
  }

  async beforeModel(transition) {
    this.tracker.initialise();
    this.intl.setLocale('en-US');
    try {
      await this.setupSessionAndRefreshIfNeeded();
    } catch (err) {
      console.debug(err);
      // Only sign the user out when the session is genuinely dead. A transient
      // failure at boot (network blip, Cognito throttling) keeps the stored
      // session and continues — the next request retries the refresh.
      if (err?.isTerminal) {
        return this.router.transitionTo('logout');
      }
    }
    await this.versionChecker.setup();
    await this.featureAccessService.setup();
    if (!this.session.isAuthenticated) {
      const routeToTransitionTo = unprotectedRoutes.includes(transition.to.name)
        ? transition.to.name
        : 'login';
      this.router.transitionTo(routeToTransitionTo);
    } else {
      const promises = [];
      promises.push(
        this.currentUser.load(),
        this.organisation.load(),
        this.roadmap.setup(),
        this.socketService.setup(),
      );
      return Promise.allSettled(promises).then(() => {
        this.chatv2.setup();
        initializeMediaClient(this.session.idToken, this.organisation.id).catch(
          () => null,
        );
        if (this.betaToggleService.showAiFeatures) {
          initializeVdrClient(this.store).catch(() => null);
        }
      });
    }
  }

  model(params) {
    if (params.roadmap_id !== undefined) {
      this.roadmap.setId(params.roadmap_id);
    }
    if (params.initiative_id !== undefined) {
      this.store
        .findRecord('initiative', params.initiative_id, {
          include: 'workstream',
        })
        .then((initiative) => {
          this.initiativeSlideover.open(initiative.id);
        })
        .catch((err) => console.debug(err));
    }
  }
}

export default ApplicationRoute;
